// Registers API — the OSINT extension's register snapshots (Typesense collections
// companies-cz / companies-sk), where they are read from, the register/tool
// catalog, and the case-graph import.
// Read-only: the snapshot is refreshed by its operator's schedule, never from
// here (ADR-0016). Partner file: src/apps/api/spectre/api/routers/v1/schemas/registers.py
import type { GraphView } from '@/types/graph-view'
import { apiClient } from './client'

export type RegisterCountry = 'cz' | 'sk'
export type CatalogCountry = 'CZ' | 'SK' | 'INTL'
export type CatalogGroup = 'snapshot' | 'official' | 'screening' | 'web'
export type SearchKind = 'all' | 'company' | 'person'
/** `hosted` = the shared register service (someone else's Typesense); `local` = this deployment's own. */
export type RegisterSourceMode = 'hosted' | 'local'

/** Where the register reads go. Address only — the search key never leaves the server. */
export interface RegisterSource {
  mode: RegisterSourceMode
  /** `protocol://host:port`, ready to show. */
  endpoint: string
}
export interface CountryStatus {
  country: RegisterCountry
  collection: string
  /** Collection exists in Typesense. */
  available: boolean
  documents: number
  companies: number
  persons: number
  versions: Record<string, string | number>
  /** The SQLite staging store exists locally. */
  storeAvailable: boolean
}

export interface CatalogEntry {
  key: string
  country: CatalogCountry
  group: CatalogGroup
  name: string
  what: string
  sources: string[]
  tool: string
  access: string
  url: string
}

export interface ToolEntry {
  name: string
  summary: string
}

export interface RegistersOverview {
  source: RegisterSource
  countries: CountryStatus[]
  catalog: CatalogEntry[]
  tools: ToolEntry[]
}

export interface OfficerHit {
  name: string
  role: string | null
  kind: 'person' | 'company'
  since: string | null
  until: string | null
  birthDate: string | null
  partyId: string
}

export interface CompanyHit {
  id: string
  ico: string | null
  label: string
  address: string | null
  type: string | null
  typeId: number
  dateStart: string | null
  dateEnd: string | null
  business: string[]
  officers: OfficerHit[]
  officersTotal: number
}

export interface EngagementHit {
  companyId: string
  company: string | null
  ico: string | null
  role: string | null
  since: string | null
  until: string | null
}

export interface PersonHit {
  id: string
  label: string
  firstName: string | null
  lastName: string | null
  address: string | null
  birthDate: string | null
  identityBasis: string | null
  engagements: EngagementHit[]
  engagementsTotal: number
}

export interface SnapshotSearchResponse {
  country: RegisterCountry
  query: string
  kind: SearchKind
  /** 1-based page; `limit` hits per kind per page; `found` carries the totals. */
  page: number
  limit: number
  found: { companies: number; persons: number }
  companies: CompanyHit[]
  persons: PersonHit[]
}

// ── register neighbourhood graph (report graph) ─────────────────────────────
export interface RegisterGraphNode {
  id: string
  label: string
  kind: 'person' | 'company'
  /** 0 = the subject, 1 = its own links, 2 = their links. */
  hop: number
  ico: string | null
  address: string | null
  birthDate: string | null
  country: string | null
  legalForm: string | null
  dateStart: string | null
  dateEnd: string | null
}

export interface RegisterGraphEdge {
  id: string
  source: string
  target: string
  role: string | null
  since: string | null
  until: string | null
}

export interface RegisterNeighbourhood {
  country: RegisterCountry
  root: string
  depth: number
  nodes: RegisterGraphNode[]
  edges: RegisterGraphEdge[]
  truncated: boolean
}

/** Body of `POST /v1/cases/{caseId}/registers/import`. */
export interface RegisterImportRequest {
  country: RegisterCountry
  /** The register document the analyst picked — `CZC<ičo>` / `CZP<serial>` / `SK…`. */
  subjectId: string
  /** `elementId` of the case entity the picker was opened from. The pick asserts
   *  the two are the same identity, so the subject folds onto this node. */
  anchorNodeId: string
  /** 1 = the subject's own officers/engagements, 2 = also theirs. */
  depth?: 1 | 2
  maxNodes?: number
}

export interface NeighbourhoodQuery {
  /** `CZC<ičo>` / `CZP<serial>` / `SKC…` / `SKP…` — or resolve a company by `ico`. */
  id?: string
  ico?: string
  country?: RegisterCountry
  depth?: 1 | 2
  maxNodes?: number
}

export const registersApi = {
  overview: () => apiClient.get<RegistersOverview>('/v1/registers/overview'),

  search: (country: RegisterCountry, q: string, kind: SearchKind = 'all', limit = 10, page = 1) =>
    apiClient.get<SnapshotSearchResponse>(`/v1/registers/${country}/search`, {
      params: { q, kind, limit, page },
    }),

  /** Write a picked subject's neighbourhood into the case graph; the returned
   *  view carries real Neo4j element ids, ready to merge into the object set. */
  importNeighbourhood: (caseId: string, body: RegisterImportRequest) =>
    apiClient.post<GraphView>(`/v1/cases/${caseId}/registers/import`, body),

  neighbourhood: (query: NeighbourhoodQuery) =>
    apiClient.get<RegisterNeighbourhood>('/v1/registers/graph', {
      params: {
        id: query.id,
        ico: query.ico,
        country: query.country,
        depth: query.depth ?? 2,
        maxNodes: query.maxNodes ?? 120,
      },
    }),
}
